// Provably fair UI: a small badge during play, the tournament's fairness record and a hand
// verifier that recomputes everything in this browser from the raw evidence (src/shared/fairness.ts).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { t, dateTime } from '../i18n/index.ts';
import { api } from '../lib/api.ts';
import { useSession } from '../lib/session.tsx';
import { observedFor, storedSeed } from '../lib/fairness.ts';
import { Dialog, ErrorView, Loading, PlayingCard } from '../components/ui.tsx';
import { verifyHand, verifySeating, type Check, type HandEvidence, type SeatingEvidence } from '../../shared/fairness.ts';
import type { Card } from '../../shared/cards.ts';

interface FairnessData {
  protocol: string;
  chainLength: number;
  serverCommitment: string;
  committedAt: number;
  status: 'open' | 'revealing' | 'active' | 'closed';
  lockedAt: number | null;
  revealDeadlineAt: number | null;
  commencedAt: number | null;
  participants: { userId: string; displayName: string; commitment: string | null; seed: string | null; revealedAt: number | null; participant: boolean }[];
  seating: SeatingEvidence | null;
  me: { commitment: string | null; mustReveal: boolean; revealed: boolean } | null;
}

/** Scrolls to #id once the content is there (links like …#reiluus from the table badge). */
function useScrollToHash(id: string, ready: boolean) {
  useEffect(() => {
    if (ready && window.location.hash === `#${id}`) document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [id, ready]);
}

const short = (hex: string | null | undefined) => (hex ? `${hex.slice(0, 10)}…${hex.slice(-6)}` : '–');

function Hex({ value, label }: { value: string | null | undefined; label: string }) {
  if (!value) return <span className="hint">–</span>;
  return (
    <code className="hex" title={value} aria-label={`${label}: ${value}`}>
      {short(value)}
    </code>
  );
}

/** "🔒 Todennettavasti reilu": a quiet badge with a plain-language explanation. */
export function FairBadge({ tournamentId, compact }: { tournamentId: string; compact?: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={`fair-badge ${compact ? 'compact' : ''}`} onClick={() => setOpen(true)} aria-label={t('fair.badge')} title={t('fair.badge')}>
        <span aria-hidden="true">🔒</span>
        {!compact && <span>{t('fair.badge')}</span>}
      </button>
      {open && (
        <Dialog title={t('fair.badge')} onClose={() => setOpen(false)}>
          <p>{t('fair.explain1')}</p>
          <p>{t('fair.explain2')}</p>
          <p className="hint">{t('fair.explain3')}</p>
          <div className="btn-row">
            <Link className="btn" to={`/turnaukset/${tournamentId}#reiluus`} onClick={() => setOpen(false)}>
              {t('fair.showRecord')}
            </Link>
            <button className="btn btn-ghost" data-close onClick={() => setOpen(false)}>
              {t('app.close')}
            </button>
          </div>
        </Dialog>
      )}
    </>
  );
}

/** Shown on the table while the players' seeds are being revealed after the start. */
export function RevealWaiting({ tournamentId, fairness }: { tournamentId: string; fairness: { participants: number; revealed: number; deadlineAt: number | null; mustReveal: boolean } }) {
  const { me } = useSession();
  const noSeedHere = fairness.mustReveal && me && !storedSeed(tournamentId, me.id);
  return (
    <div className="reveal-wait" role="status">
      <strong>{t('fair.waitingTitle')}</strong>
      <span>{t('fair.waitingBody', { n: fairness.revealed, total: fairness.participants })}</span>
      {fairness.deadlineAt && <span className="hint">{t('fair.waitingDeadline', { time: dateTime(fairness.deadlineAt) })}</span>}
      {noSeedHere && <span className="reveal-warn">{t('fair.noSeedHere')}</span>}
    </div>
  );
}

/** The tournament's fairness record: commitments, seeds and the seating draw. */
export function FairnessSection({ tournamentId }: { tournamentId: string }) {
  const { me } = useSession();
  const q = useQuery({ queryKey: ['fairness', tournamentId], queryFn: () => api.get<{ fairness: FairnessData | null }>(`/api/tournaments/${tournamentId}/fairness`) });
  const seating = useMemo(() => (q.data?.fairness?.seating ? verifySeating(q.data.fairness.seating) : null), [q.data]);
  useScrollToHash('reiluus', q.isSuccess);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const f = q.data.fairness;
  if (!f) {
    return (
      <section id="reiluus" className="fair-section">
        <h3>{t('fair.sectionTitle')}</h3>
        <p className="hint">{t('fair.legacy')}</p>
      </section>
    );
  }
  const local = me ? storedSeed(tournamentId, me.id) : null;
  const serverSaysMissing = f.status !== 'open' && f.status !== 'revealing' && f.me && f.me.commitment && !f.me.revealed;
  return (
    <section id="reiluus" className="fair-section">
      <h3>
        <span aria-hidden="true">🔒 </span>
        {t('fair.sectionTitle')}
      </h3>
      <p>{t('fair.explain1')}</p>
      <dl className="kv fair-kv">
        <div>
          <dt>{t('fair.protocol')}</dt>
          <dd>{f.protocol}</dd>
        </div>
        <div>
          <dt>{t('fair.serverCommitment')}</dt>
          <dd>
            <Hex value={f.serverCommitment} label={t('fair.serverCommitment')} />
            <span className="hint"> {t('fair.committedAt', { time: dateTime(f.committedAt) })}</span>
          </dd>
        </div>
        <div>
          <dt>{t('fair.status')}</dt>
          <dd>{t(`fair.statuses.${f.status}`)}</dd>
        </div>
        {f.me?.commitment && (
          <div>
            <dt>{t('fair.yourCommitment')}</dt>
            <dd>
              <Hex value={f.me.commitment} label={t('fair.yourCommitment')} />{' '}
              <span className="hint">{local ? (local.commitment === f.me.commitment ? t('fair.seedOnDevice') : t('fair.seedOtherDevice')) : t('fair.seedNotHere')}</span>
            </dd>
          </div>
        )}
      </dl>
      {serverSaysMissing && local?.revealedAt && (
        <p className="alert alert-warn" role="alert">
          {t('fair.lostReveal', { time: dateTime(local.revealedAt) })}
        </p>
      )}
      {f.participants.length > 0 && f.status !== 'open' && (
        <div className="table-scroll">
          <table className="data-table fair-table">
            <thead>
              <tr>
                <th>{t('fair.player')}</th>
                <th>{t('fair.commitment')}</th>
                <th>{t('fair.seed')}</th>
              </tr>
            </thead>
            <tbody>
              {f.participants.map((p) => (
                <tr key={p.userId}>
                  <td>{p.displayName}</td>
                  <td>
                    <Hex value={p.commitment} label={t('fair.commitment')} />
                  </td>
                  <td>
                    {p.seed ? (
                      <Hex value={p.seed} label={t('fair.seed')} />
                    ) : (
                      <span className="hint">{f.status === 'revealing' ? t('fair.notYet') : p.commitment ? t('fair.notRevealed') : t('fair.noCommitment')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {seating && (
        <p className={`fair-line ${seating.ok ? 'ok' : 'fail'}`}>
          <span aria-hidden="true">{seating.ok ? '✓' : '✗'}</span> {seating.ok ? t('fair.seatingOk') : t('fair.seatingFail')}
        </p>
      )}
    </section>
  );
}

const CHECK_TITLE = {
  protocol: 'fair.checks.protocol',
  server: 'fair.checks.server',
  players: 'fair.checks.players',
  table: 'fair.checks.table',
  entropy: 'fair.checks.entropy',
  deck: 'fair.checks.deck',
  dealt: 'fair.checks.dealt',
  history: 'fair.checks.history',
  observed: 'fair.checks.observed',
} as const satisfies Record<Check['key'], string>;
const STATUS_ICON = { ok: '✓', fail: '✗', warn: '!', pending: '…' } as const;

/**
 * Recomputes a hand from the raw evidence in this browser. `history` is the hand history this
 * page already shows (board and shown hands), so the comparison does not rely on the evidence alone.
 */
export function HandVerifier({ tournamentId, handNo, history }: { tournamentId: string; handNo: number; history?: HandEvidence['history'] }) {
  const { me } = useSession();
  // The locked participants, to check that this hand used exactly them.
  const record = useQuery({ queryKey: ['fairness', tournamentId], queryFn: () => api.get<{ fairness: FairnessData | null }>(`/api/tournaments/${tournamentId}/fairness`) });
  const q = useQuery({
    queryKey: ['hand-fairness', tournamentId, handNo],
    queryFn: () => api.get<{ evidence: HandEvidence | null; handStatus: string }>(`/api/tournaments/${tournamentId}/hands/${handNo}/fairness`),
  });
  const [showDeck, setShowDeck] = useState(false);
  useScrollToHash('tarkista', q.isSuccess);
  const result = useMemo(() => {
    const ev = q.data?.evidence;
    if (!ev) return null;
    const evidence = history && ev.serverSeed ? { ...ev, history } : ev;
    const obs = me ? observedFor(tournamentId, me.id, handNo) : null;
    const own = me ? storedSeed(tournamentId, me.id) : null;
    return verifyHand(
      evidence,
      {
        ...(obs ?? {}),
        // Only a seed the server confirmed receiving counts: before that it may legitimately be absent.
        ...(me && own?.revealedAt && { ownSeed: { userId: me.id, seed: own.seed } }),
      },
      record.data?.fairness?.seating?.participants ?? null,
    );
  }, [q.data, record.data, history, me, tournamentId, handNo]);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorView error={q.error} onRetry={() => q.refetch()} />;
  const ev = q.data.evidence;
  if (!ev || !result) return <p className="hint">{t('fair.legacyHand')}</p>;
  const pending = result.checks.some((c) => c.status === 'pending');
  const failed = result.checks.some((c) => c.status === 'fail');
  const download = () => {
    const blob = new Blob([JSON.stringify(ev, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `jako-${handNo}-todisteet.json`;
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <section className="verifier" aria-live="polite">
      <p className={`verdict ${failed ? 'fail' : pending ? 'pending' : 'ok'}`}>
        <span aria-hidden="true">{failed ? '✗' : pending ? '…' : '✓'}</span>
        {failed ? t('fair.verdictFail') : pending ? t('fair.verdictPending') : t('fair.verdictOk')}
      </p>
      <ul className="checks">
        {result.checks.map((c) => (
          <li key={c.key} className={`check ${c.status}`}>
            <span className="check-icon" aria-hidden="true">
              {STATUS_ICON[c.status]}
            </span>
            <span className="check-text">
              <strong>{t(CHECK_TITLE[c.key])}</strong>
              <span className="hint">{t(`fair.status_${c.status}`)}</span>
              {c.status !== 'ok' && <span className="check-detail">{c.detail}</span>}
            </span>
          </li>
        ))}
      </ul>
      <dl className="kv fair-kv">
        <div>
          <dt>{t('fair.hand')}</dt>
          <dd>
            #{ev.handNo} ({t('fair.chainPos', { n: ev.chainIndex })})
          </dd>
        </div>
        <div>
          <dt>{t('fair.serverCommitment')}</dt>
          <dd>
            <Hex value={ev.serverCommitment} label={t('fair.serverCommitment')} />
          </dd>
        </div>
        <div>
          <dt>{t('fair.serverSeed')}</dt>
          <dd>
            <Hex value={ev.serverSeed} label={t('fair.serverSeed')} />
          </dd>
        </div>
        <div>
          <dt>{t('fair.entropy')}</dt>
          <dd>
            <Hex value={result.entropy} label={t('fair.entropy')} />
          </dd>
        </div>
        {result.deckDigest && (
          <div>
            <dt>{t('fair.deckDigest')}</dt>
            <dd>
              <Hex value={result.deckDigest} label={t('fair.deckDigest')} />
            </dd>
          </div>
        )}
      </dl>
      {result.deck && ev.dealt && (
        <>
          <button type="button" className="btn btn-small" aria-expanded={showDeck} onClick={() => setShowDeck((v) => !v)}>
            {showDeck ? t('fair.hideDeck') : t('fair.showDeck')}
          </button>
          {showDeck && (
            <div className="deck-grid" aria-label={t('fair.deckOrder')}>
              {result.deck.map((c: Card, i: number) => (
                <span key={c} className="deck-cell">
                  <small>{i + 1}</small>
                  <PlayingCard card={c} size="s" />
                </span>
              ))}
            </div>
          )}
        </>
      )}
      <div className="btn-row" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-small" onClick={download}>
          {t('fair.downloadEvidence')}
        </button>
      </div>
      <p className="hint">{t('fair.independent')}</p>
    </section>
  );
}
